import type { SocialProviderName } from '@repo/auth-contracts';
import type { ReactNode } from 'react';
import type { Copy } from '@/lib/messages';
import { Button } from './button';
import { AppleMark, GoogleMark, MicrosoftMark } from './provider-marks';

const LABELS: Record<SocialProviderName, string> = {
  google: 'Google',
  microsoft: 'Microsoft',
  apple: 'Apple',
};

/**
 * Display order, independent of whatever order the server lists them in.
 * Which providers appear is the server's call and the client must not guess
 * (docs/auth/40-features/login-social.md); the order they appear in is
 * presentation, and belongs here. Anything unrecognised keeps its
 * server-given position at the end rather than vanishing.
 */
const ORDER: SocialProviderName[] = ['google', 'apple', 'microsoft'];

function inDisplayOrder(providers: SocialProviderName[]): SocialProviderName[] {
  const known = ORDER.filter((provider) => providers.includes(provider));
  const rest = providers.filter((provider) => !ORDER.includes(provider));
  return [...known, ...rest];
}

const MARKS: Record<SocialProviderName, ReactNode> = {
  google: <GoogleMark />,
  microsoft: <MicrosoftMark />,
  apple: <AppleMark />,
};

interface SocialButtonsProps {
  copy: Copy;
  /** Server-driven: only providers with a complete credential set arrive here. */
  providers: SocialProviderName[];
  pending: boolean;
  onSelect: (provider: SocialProviderName) => void;
}

/**
 * Rendered as plain `outline` buttons, identical to the passkey one beside
 * them: these are alternative ways to do the same thing, and styling them
 * differently would imply a ranking that does not exist.
 */
export function SocialButtons({
  copy,
  providers,
  pending,
  onSelect,
}: SocialButtonsProps) {
  if (providers.length === 0) return null;

  return (
    <>
      {inDisplayOrder(providers).map((provider) => (
        <Button
          key={provider}
          type="button"
          variant="outline"
          icon={MARKS[provider]}
          onClick={() => onSelect(provider)}
          disabled={pending}
        >
          {copy.socialContinueWith(LABELS[provider])}
        </Button>
      ))}
    </>
  );
}
